import { PrimaryButton } from '@designsforhealth/dfh-react-components'
import { faImage } from '@fortawesome/free-solid-svg-icons'
import { CircularProgress } from '@material-ui/core'
import { getImageDimensions } from '@sanity/asset-utils'
import React, {
  ChangeEvent,
  DragEvent,
  HTMLAttributes,
  ImgHTMLAttributes,
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'
import { map, startWith } from 'rxjs'

import { SanityImage } from '../../../../../../groq/sanity-dispensaries/types'
import { useLogAndCaptureLeftResult } from '../../../../../../hooks/useLogAndCaptureLeftResult'
import { FetchResult } from '../../../../../../lib/fetch'
import { dispensaryImageUrlFor } from '../../../../../../lib/sanity'
import { fromAssetCreateRequest } from '../../../../../../lib/sanity-dispensaries/manage/asset'
import { constEmptyResult } from '../../../../../../utils/constant'
import * as Styled from './styled'

export interface ImageEditorProps {
  sanityValue?: string
  handleSubmit: ((assetRef: string) => Promise<void>) | ((assetRef: string) => void)
  loading?: boolean
  submitOnChange?: boolean
  submitDisabled?: boolean
  noRemove?: boolean
  aspectRatio?: 'logo' | 'hero' | 'contact'
  defaultImagePreviewComponent?: React.VFC
  previewContainerComponent?: 'div' | React.FC<HTMLAttributes<HTMLDivElement>>
  imagePreviewComponent?: 'img' | React.FC<ImgHTMLAttributes<HTMLImageElement>>
}

type ImageEditorAction =
  | {
      type: 'asset_uploaded'
      value: SanityImage
    }
  | { type: 'asset_removed' }
  | { type: 'sanity_value_changed' }
  | { type: 'drag_active' }
  | { type: 'drag_inactive' }
  | { type: 'unsupported_file_selected' }
  | { type: 'file_too_large' }
  | { type: 'multiple_files_selected' }
  | { type: 'file_validated' }

interface ImageEditorState {
  aspectRatioWarning: string
  dragActive: boolean
  enableSubmit: boolean
  validationError: string
  value: string
}

const aspectRatioWarningFor = ({
  aspectRatio,
  value,
}: Pick<ImageEditorProps, 'aspectRatio'> & { value: string }): string => {
  const { aspectRatio: imageAspectRatio } = getImageDimensions(value)
  if (aspectRatio === 'logo') {
    if (imageAspectRatio < 1) {
      return 'Logo images should be square, or have up to 5x greater width than height. Try a wider image.'
    } else if (imageAspectRatio > 5) {
      return 'Logo images should be square, or have up to 5x greater width than height. Try a narrower image.'
    }
  } else if (aspectRatio === 'hero') {
    if (imageAspectRatio <= 1) {
      return 'Banner images should have a greater width than height. Try a wider image.'
    } else if (imageAspectRatio > 5) {
      return 'Banner images should have up to 5x greater width than height. Try a narrower image.'
    }
  } else if (aspectRatio === 'contact') {
    if (imageAspectRatio <= 1) {
      return 'Contact images should have a greater width than height. Try a wider image.'
    } else if (imageAspectRatio > 5) {
      return 'Contact images should have up to 5x greater width than height. Try a narrower image.'
    }
  }
  return ''
}

const reducer =
  ({
    aspectRatio,
    sanityValue,
  }: Pick<ImageEditorProps, 'aspectRatio' | 'sanityValue'>): Reducer<
    ImageEditorState,
    ImageEditorAction
  > =>
  (prevState, action) => {
    switch (action.type) {
      case 'asset_uploaded': {
        const value = action.value.asset._ref
        return value === prevState.value
          ? prevState
          : {
              ...prevState,
              aspectRatioWarning: aspectRatioWarningFor({ aspectRatio, value }),
              enableSubmit: (sanityValue ?? '') !== value,
              value,
            }
      }
      case 'asset_removed': {
        const value = ''
        return value === prevState.value
          ? prevState
          : {
              ...prevState,
              aspectRatioWarning: '',
              enableSubmit: (sanityValue ?? '') !== value,
              validationError: '',
              value,
            }
      }
      case 'drag_active':
        return prevState.dragActive
          ? prevState
          : {
              ...prevState,
              dragActive: true,
            }
      case 'drag_inactive':
        return prevState.dragActive
          ? {
              ...prevState,
              dragActive: false,
            }
          : prevState
      case 'file_validated':
        return prevState.validationError
          ? {
              ...prevState,
              validationError: '',
            }
          : prevState
      case 'unsupported_file_selected':
        return {
          ...prevState,
          validationError:
            'File type not supported. Accepted image types: GIF, JPG, PNG, SVG, and TIFF.',
        }
      case 'file_too_large':
        return {
          ...prevState,
          validationError: 'File size exceeds the maximum allowed size of 5MB.',
        }
      case 'multiple_files_selected':
        return {
          ...prevState,
          validationError:
            'Uploading multiple files not supported. Please select a single image instead.',
        }
      case 'sanity_value_changed': {
        const enableSubmit = prevState.value !== (sanityValue ?? '')
        return enableSubmit === prevState.enableSubmit
          ? prevState
          : {
              ...prevState,
              enableSubmit,
            }
      }
      default:
        return prevState
    }
  }

const allowedMimeTypes = ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/tiff']
const maximumFileSize = 5 * 1048000

const ImageEditor: React.FC<ImageEditorProps> = ({
  sanityValue,
  handleSubmit,
  loading = false,
  submitDisabled = false,
  submitOnChange = false,
  noRemove = false,
  aspectRatio,
  defaultImagePreviewComponent: DefaultImagePreviewComponent,
  imagePreviewComponent: ImagePreviewComponent = Styled.ImagePreview,
  previewContainerComponent: PreviewContainerComponent = Styled.ImagePreviewContainer,
}) => {
  const [{ aspectRatioWarning, dragActive, enableSubmit, validationError, value }, dispatch] =
    useReducer(
      useMemo(() => reducer({ aspectRatio, sanityValue }), [aspectRatio, sanityValue]),
      {
        aspectRatioWarning: '',
        dragActive: false,
        enableSubmit: false,
        validationError: '',
        value: sanityValue || '',
      },
    )

  const [uploadInput, setUploadInput] = useState<File>()
  const [{ uploadLoading, uploadResult }, setUploadState] = useState<{
    uploadLoading: boolean
    uploadResult?: FetchResult<SanityImage>
  }>({ uploadLoading: false })

  useEffect(() => {
    if (!uploadInput) {
      setUploadState((prevState) =>
        !prevState.uploadLoading && !prevState.uploadResult
          ? prevState
          : {
              uploadLoading: false,
              uploadResult: undefined,
            },
      )
      return
    }
    const subscription = fromAssetCreateRequest(uploadInput)
      .pipe(
        map((result) => ({
          uploadLoading: false,
          uploadResult: result,
        })),
        startWith({
          uploadLoading: true,
          uploadResult: undefined,
        }),
      )
      .subscribe((uploadState) => {
        setUploadState(uploadState)
      })
    return () => subscription.unsubscribe()
  }, [uploadInput])

  const inputRef = React.useRef<HTMLInputElement>(null)
  const outerRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    dispatch({ type: 'sanity_value_changed' })
  }, [sanityValue])

  useLogAndCaptureLeftResult(uploadResult ?? constEmptyResult())
  useEffect(() => {
    if (uploadResult?._tag === 'Right') {
      dispatch({ type: 'asset_uploaded', value: uploadResult.data })
    }
  }, [uploadResult])

  useEffect(() => {
    if (enableSubmit && submitOnChange) {
      handleSubmit(value)
    }
  }, [enableSubmit, handleSubmit, submitOnChange, value])

  const handleDrag = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (e.type === 'dragenter' || e.type === 'dragover') {
      dispatch({ type: 'drag_active' })
      e.preventDefault()
      e.stopPropagation()
    } else if (e.type === 'dragend') {
      dispatch({ type: 'drag_inactive' })
      e.preventDefault()
      e.stopPropagation()
    } else if (e.type === 'dragleave' && e.target === outerRef.current) {
      dispatch({ type: 'drag_inactive' })
      e.preventDefault()
      e.stopPropagation()
    }
  }, [])

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch({ type: 'drag_inactive' })
    const { files } = e.dataTransfer
    if (files.length > 1) {
      dispatch({ type: 'multiple_files_selected' })
      return
    }
    const file = files[0]
    if (!file) {
      return
    }
    if (!allowedMimeTypes.includes(file.type)) {
      dispatch({ type: 'unsupported_file_selected' })
      return
    }
    if (file.size >= maximumFileSize) {
      dispatch({ type: 'file_too_large' })
      return
    }
    dispatch({ type: 'file_validated' })
    setUploadInput(file)
  }, [])

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    const { files } = e.target ?? {}
    if (!files || !files.length) {
      return
    }
    if (files.length > 1) {
      dispatch({ type: 'multiple_files_selected' })
      return
    }
    const file = files[0]
    if (!allowedMimeTypes.includes(file.type)) {
      dispatch({ type: 'unsupported_file_selected' })
      return
    }
    if (file.size >= maximumFileSize) {
      dispatch({ type: 'file_too_large' })
      return
    }
    dispatch({ type: 'file_validated' })
    setUploadInput(file)
  }, [])

  const onBrowseClick = useCallback(() => {
    inputRef?.current?.click()
  }, [])

  const onRemoveClick = useCallback(() => {
    dispatch({ type: 'asset_removed' })
  }, [])

  const onSubmit = useCallback(() => {
    handleSubmit(value)
  }, [handleSubmit, value])

  const previewElement = useMemo(
    () =>
      value ? (
        <PreviewContainerComponent>
          <ImagePreviewComponent
            alt="Image preview"
            src={
              dispensaryImageUrlFor({ _ref: value })
                .ignoreImageParams()
                .width(600)
                .fit('max')
                .dpr(2)
                .auto('format')
                .url() || ''
            }
          />
        </PreviewContainerComponent>
      ) : DefaultImagePreviewComponent ? (
        <PreviewContainerComponent>
          <DefaultImagePreviewComponent />
        </PreviewContainerComponent>
      ) : null,
    [DefaultImagePreviewComponent, ImagePreviewComponent, PreviewContainerComponent, value],
  )

  return (
    <>
      <Styled.OuterContainer
        $dragActive={dragActive}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        ref={outerRef}
      >
        <Styled.HiddenInput
          ref={inputRef}
          type="file"
          multiple={false}
          accept=".gif, .jpeg, .jpg, .png, .svg, .tif, .tiff"
          onChange={handleChange}
        />
        {uploadLoading ? (
          <Styled.InnerContainer $dragActive={dragActive}>
            <CircularProgress size={40} color="inherit" />
          </Styled.InnerContainer>
        ) : (
          <Styled.InnerContainer $dragActive={dragActive}>
            {previewElement}
            <Styled.Actions $isPreviewVisible={!!previewElement}>
              <Styled.DragText>
                <Styled.ImageIcon icon={faImage} />
                {`${dragActive ? 'Drop' : 'Drag'} Image Here`}
              </Styled.DragText>
              {value && <Styled.DragText>to Replace</Styled.DragText>}
              <Styled.OrText>OR</Styled.OrText>
              <Styled.Button $dragActive={dragActive} onClick={onBrowseClick} text="Browse Files" />
              <Styled.FileInformationText>Max. file size 5MB.</Styled.FileInformationText>
              {value && !noRemove && (
                <>
                  <Styled.OrText>OR</Styled.OrText>
                  <Styled.RemoveButton
                    $dragActive={dragActive}
                    onClick={onRemoveClick}
                    text="Remove"
                  />
                </>
              )}
            </Styled.Actions>
          </Styled.InnerContainer>
        )}
      </Styled.OuterContainer>
      {aspectRatioWarning && (
        <Styled.StyledAlert type="attention">
          The aspect ratio of this file is not supported. {aspectRatioWarning} You can continue, but
          the display may not be optimal.
        </Styled.StyledAlert>
      )}
      {!submitOnChange && (
        <PrimaryButton
          disabled={!enableSubmit || loading || uploadLoading || submitDisabled}
          onClick={onSubmit}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : enableSubmit ? (
            'Publish Changes'
          ) : (
            'No Changes to Publish'
          )}
        </PrimaryButton>
      )}
      {validationError && <Styled.StyledAlert type="error">{validationError}</Styled.StyledAlert>}
      {uploadResult?._tag === 'Left' && (
        <Styled.StyledAlert type="error">
          We were unable to upload your file, please try again soon.
        </Styled.StyledAlert>
      )}
    </>
  )
}

export default ImageEditor
